import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, impersonate, startImpersonation, getImpersonation } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';

interface Props {
  user: AuthUser | null;
  login: (userId: string, token: string, userData: Record<string, unknown>) => void;
  logout: () => void;
}

interface CrewUser { id: string; user_name: string; employee_code?: string; user_role: string; vessel_name?: string | null }

/** 管理者が船員アカウントで操作（教育・デモ用）。
 *  - 代理ログイン中: 上部に警告バナー＋「デモ終了」
 *  - 管理者かつ通常時: 右下に「船員デモ」ボタン → 船員を選んで代理ログイン */
export const ImpersonationBar: React.FC<Props> = ({ user, login, logout }) => {
  const nav = useNavigate();
  const imp = getImpersonation();
  const isAdmin = !!user && ['shore_admin', 'system_admin'].includes(user.user_role);
  const [open, setOpen] = useState(false);
  const [crew, setCrew] = useState<CrewUser[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    apiFetch<{ data: CrewUser[] }>('/users?limit=500')
      .then(r => setCrew((r.data ?? []).filter(u => ['crew', 'captain'].includes(u.user_role))))
      .catch(() => setCrew([]));
  }, [open]);

  const doImpersonate = async (c: CrewUser) => {
    setBusy(c.id);
    try {
      const d = await impersonate(c.id);
      startImpersonation(d.access_token, d.impersonated_by);
      login(String(d.user['user_id'] ?? c.id), d.access_token, d.user);
      setOpen(false);
      nav('/home', { replace: true });
    } catch (e: any) {
      alert(e?.message ?? '代理ログインに失敗しました');
    } finally { setBusy(null); }
  };

  // 代理ログイン中は画面上部に固定の帯が乗るぶん、各画面のヘッダーを下げる
  // （帯は position:fixed で中身を押し下げないため、CSS変数で余白を足す）
  const impersonating = !!imp;
  useEffect(() => {
    const root = document.documentElement;
    if (impersonating) root.dataset['impersonating'] = '1';
    else delete root.dataset['impersonating'];
    return () => { delete root.dataset['impersonating']; };
  }, [impersonating]);

  // ── 代理ログイン中バナー ──
  if (imp) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
        background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', gap: 8,
        padding: 'calc(max(var(--app-safe-top, 20px), env(safe-area-inset-top, 0px)) + 4px) 12px 4px', fontSize: 12, fontWeight: 700,
      }}>
        <span style={{ fontSize: 14 }}>🎭</span>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          デモ操作中：<strong>{user?.user_name}</strong> として操作（管理者 {imp.by}）
        </span>
        <button onClick={logout}
          style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 999, padding: '3px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>
          デモ終了
        </button>
      </div>
    );
  }

  // ── 管理者向け: 船員デモ開始ボタン ──
  if (!isAdmin) return null;

  const filtered = q.trim()
    ? crew.filter(c => c.user_name.includes(q) || (c.employee_code ?? '').includes(q))
    : crew;

  return (
    <>
      {/* 画面の内容（タイル等）に被らないよう右下・小さめ・半透明にする（2026-10-19） */}
      <button onClick={() => setOpen(true)} title="船員アカウントで操作（教育・デモ）"
        style={{
          position: 'fixed', right: 12, bottom: 84, zIndex: 900,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'rgba(124,58,237,0.88)', color: '#fff', border: 'none', borderRadius: 999,
          padding: '7px 12px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
          boxShadow: '0 3px 10px rgba(124,58,237,0.35)',
        }}>
        🎭 デモ
      </button>

      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 480, maxHeight: '82vh', background: '#fff', borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px 10px' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>🎭 船員として操作（デモ）</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                選んだ船員のアカウントで操作します。打刻など実際の操作ができます（教育・実演用）。上部に「デモ操作中」と表示され、「デモ終了」でログアウトします。
              </div>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="名前・社員コードで検索"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', background: '#f8fafc' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 16px' }}>
              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', fontSize: 13 }}>該当する船員がいません</div>
              ) : filtered.map(c => (
                <button key={c.id} onClick={() => doImpersonate(c)} disabled={!!busy}
                  style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: busy === c.id ? '#f5f3ff' : '#fff', cursor: 'pointer' }}>
                  <span style={{ width: 34, height: 34, borderRadius: '50%', background: c.user_role === 'captain' ? 'linear-gradient(135deg,#b45309,#d97706)' : 'linear-gradient(135deg,#0ea5e9,#6366f1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>{c.user_name.slice(0, 1)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{c.user_name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>{c.employee_code}{c.vessel_name ? `・${c.vessel_name}` : ''}</span>
                  </span>
                  <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 800, flexShrink: 0 }}>{busy === c.id ? '…' : 'この人で'}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setOpen(false)}
              style={{ padding: 14, border: 'none', borderTop: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              閉じる
            </button>
          </div>
        </div>
      )}
    </>
  );
};
